import React, {
  Fragment,
  useState,
  useEffect,
  forwardRef,
  useRef,
  useContext,
  useMemo,
} from "react";

import clsx from "clsx";
import moment from "moment";
import { QueryBuilderContext } from "contexts/QueryBuilderContextProvider";
import Legend from "components/QueryBuilder/Elements/Legend";

import {
  BarChart,
  Bar,
  Tooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  ErrorBar,
} from "recharts";

import {
  MAIN_COLORS,
  selectFormat,
  StandardTooltip,
} from "components/Utils/Utils.js";

import {
  formatQueryMetric,
  getPredictionBarShape,
  formatDate,
} from "components/QueryBuilder//Utils/QueryBuilderUtils";
import QueryBuilderXAxis from "components/QueryBuilder/Elements/QueryBuilderXAxis";
import {
  containsToolTipData,
  excludePreditionMetrics,
  extractPredictionData,
  isDataKeyAPrediction,
  metricIsNotAPrediction,
} from "components/Utils/ChartsDataUtils";

import { formatDataForChart } from "components/Utils/ChartsDataUtils";
import QueryBuilderTooltipPrediction from "./tooltips/QueryBuilderTooltipPrediction";
import QueryBuilderTooltipMetric from "./tooltips/QueryBuilderTooltipMetric";

const QueryBuilderBarChart = forwardRef((props, ref) => {
  const { state: globalState } = useContext(QueryBuilderContext);

  // Trended Views
  const { chartData, all_dim_keys, dimensionsMap } = useMemo(
    () => formatDataForChart(props.dataGroupedByDate),
    [props.dataGroupedByDate]
  );

  const [singleTooltipData, setTooltipData] = useState(null);
  const [seriesFilter, setSeriesFilter] = useState(null);
  const [state, setState] = useState({
    colors: {},
    lData: [],
  });

  const refChart = useRef(null);

  const [counterLoadMore, setCounterLoadMore] = useState(1);
  const LOAD_MORE_VALUE = 50;

  const { lData, colors } = state;

  useEffect(() => {
    const lData = [
      {
        name: "metrics",
        friendlyName: "Metrics",
        value: [
          {
            name: "select_all",
            friendlyName: "Select all",
            color: "#8a9ca3",
            checked: true,
          },
        ],
      },
    ];

    Object.keys(props.metricMap).forEach((metric) =>
      lData[0].value.push({
        name: props.metricMap[metric].name,
        friendlyName: props.metricMap[metric].friendlyName,
        color: "#38474d",
        checked: true,
      })
    );

    const colors = {};
    props.metrics.forEach((metric, j) => {
      const metrics = [
        {
          name: "select_all",
          friendlyName: "Select all",
          color: "#8a9ca3",
          checked: true,
        },
      ];

      if (all_dim_keys && all_dim_keys.length) {
        all_dim_keys.forEach((dim_key, i) => {
          colors[metric + ":" + dim_key] =
            MAIN_COLORS[(j * all_dim_keys.length + i) % 20];
          metrics.push({
            name: metric + ":" + dim_key,
            friendlyName: dim_key
              .split(":")
              .map((d) => d.charAt(0).toUpperCase() + d.slice(1))
              .join(" | "),
            color: colors[metric + ":" + dim_key],
            checked: true,
          });
        });

        lData.push({
          name: props.metricMap[metric].name,
          friendlyName: props.metricMap[metric].friendlyName,
          value: metrics,
        });
      } else {
        colors[metric] = MAIN_COLORS[j % 20];
        lData[0].value.find((d) => d.name === metric).color = colors[metric];
      }
    });

    setState({
      ...state,
      colors,
      lData,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderBars = () => {
    /**
     * Shared component to render bars for predictions
     * @param {*} metric
     * @param {*} isFixedBarWidth
     * @param {*} dimensionKey to be set when being used with dimension
     * @returns
     */
    const renderPredictionBar = (
      metric,
      isFixedBarWidth,
      dimensionKey = "",
      showUncertainty
    ) => (
      <Bar
        // FIXME: This is an workaround to get the label being re-rendered properly.
        key={Math.random() * 1}
        isAnimationActive={false}
        dataKey={metric + dimensionKey + ".pred"}
        fill={`${colors[metric + dimensionKey]}`}
        barSize={isFixedBarWidth ? 35 : null}
        onMouseOver={(d) => !singleTooltipData && setTooltipData(d)}
        onMouseOut={() => setTooltipData(null)}
        shape={({ x, y, width, height }) => {
          const BarShape = getPredictionBarShape(
            x,
            y,
            width,
            height,
            metric + dimensionKey + ".pred"
          );
          return (
            <g>
              <BarShape
                fillOpacity={1}
                fill={`${colors[metric + dimensionKey]}`}
                stroke="3 3"
                dataKey={metric + dimensionKey + ".pred"}
              />
            </g>
          );
        }}
      >
        {showUncertainty && (
          <ErrorBar
            dataKey={metric + dimensionKey + ".deviation"}
            width={4}
            strokeWidth={1}
            stroke="gray"
          />
        )}
      </Bar>
    );
    let bars = [];
    let isFixedBarWidth = null;
    // ignores the metrics that comes from the prediction
    props.metrics.filter(excludePreditionMetrics).forEach((metric, j) => {
      if (all_dim_keys && all_dim_keys.length) {
        // metrics + dimensions
        all_dim_keys.forEach((dim_key, i) => {
          if (
            !seriesFilter ||
            (seriesFilter && seriesFilter.includes(metric + ":" + dim_key))
          ) {
            isFixedBarWidth = all_dim_keys.length <= 7;
            bars.push(
              <Bar
                key={`bar_${metric + ":" + dim_key}`}
                dataKey={metric + ":" + dim_key}
                fill={`${colors[metric + ":" + dim_key]}`}
                barSize={isFixedBarWidth ? 35 : null}
                onMouseOver={(d) => {
                  !singleTooltipData && setTooltipData(d);
                }}
                onMouseOut={() => setTooltipData(null)}
              />
            );
            // only add pred charts when requested
            if (globalState.loadPredictions) {
              // adds the bar metric prediction
              bars.push(
                renderPredictionBar(
                  metric,
                  isFixedBarWidth,
                  ":" + dim_key,
                  globalState.viewUncertainty
                )
              );
            }
          }
        });
      } else {
        // only metrics
        if (!seriesFilter || (seriesFilter && seriesFilter.includes(metric))) {
          isFixedBarWidth = all_dim_keys.length <= 7;
          bars.push(
            <Bar
              key={`bar_${metric}`}
              dataKey={metric}
              fill={`${colors[metric]}`}
              barSize={isFixedBarWidth ? 35 : null}
              onMouseOver={(d) => !singleTooltipData && setTooltipData(d)}
              onMouseOut={() => setTooltipData(null)}
            />
          );

          // only add pred charts when requested
          if (globalState.loadPredictions) {
            // adds the bar metric prediction
            bars.push(
              renderPredictionBar(
                metric,
                isFixedBarWidth,
                "",
                globalState.viewUncertainty
              )
            );
          }
        }
      }
    });

    return bars;
  };

  const allBars = renderBars();
  const numberOfBars = LOAD_MORE_VALUE * counterLoadMore;

  const onChangeSelection = (selection) => {
    const checkedMetrics = selection.metrics
      .slice(1)
      .filter((d) => d.checked)
      .map((d) => d.name);
    const _seriesFilter = checkedMetrics.slice(0);
    checkedMetrics.forEach(
      (m) =>
        selection[m] &&
        selection[m]
          .slice(1)
          .forEach((d) => d.checked && _seriesFilter.push(d.name))
    );
    setSeriesFilter(_seriesFilter);
  };

  const chartWidth = refChart.current ? refChart.current.offsetWidth / 43 : "";
  return (
    <div className="row">
      <div
        ref={refChart}
        className="col-10 query-view-charts query-view-border pt-4 pb-4 pl-3 pr-3"
      >
        {allBars.length > numberOfBars ? (
          <div className="query-view-load-more mr-3 d-flex align-items-baseline">
            <span>{numberOfBars} items displayed</span>
            <button
              className="btn btn-link nav__item nav__item--sm hidden-md-down ml-3"
              onClick={() => setCounterLoadMore(counterLoadMore + 1)}
            >
              Load more
            </button>
          </div>
        ) : (
          ""
        )}

        <ResponsiveContainer ref={ref} height={500} width="100%">
          <BarChart data={chartData} barCategoryGap={0}>
            <CartesianGrid
              stroke="#FEE5DE"
              strokeDasharray="3 3"
              vertical={false}
            />
            {allBars.slice(
              0,
              allBars.length > numberOfBars ? numberOfBars : allBars.length
            )}
            <YAxis
              axisLine={false}
              tickLine={false}
              mirror={true}
              tickFormatter={(d) =>
                props.formatType ? selectFormat(props.formatType, d) : d
              }
            />
            <XAxis
              dataKey="date"
              axisLine={false}
              tickLine={false}
              interval={
                chartData.length < chartWidth
                  ? 0
                  : Math.round(chartData.length / chartWidth)
              }
              minTickGap={20}
              padding={{ left: 20, right: 20 }}
              tick={<QueryBuilderXAxis granularity={props.granularity} />}
            />
            <Tooltip
              cursor={{ fill: "#eee", opacity: 0.5 }}
              content={
                <StandardTooltip
                  render={() => {
                    if (singleTooltipData) {
                      const arrNames = Object.keys(singleTooltipData.payload);
                      const value = singleTooltipData.value;
                      const dataKey = arrNames.find(
                        (el) => singleTooltipData.payload[el] === +value
                      );

                      const isPrediction = isDataKeyAPrediction(dataKey);

                      const tooltipData = {
                        date: singleTooltipData.payload.date,
                        [dataKey]: value,
                      };

                      const currentDatePoint = tooltipData["date"];

                      const renderItem = (index, parent) => {
                        const dim = props.dimensions[index];

                        return dim.values.map((dim_key, i) => {
                          const dimFriendlyName = dimensionsMap
                            .map((dimensions) =>
                              dimensions.find((dim) => dim.name === dim_key)
                            )
                            .filter((o) => o !== undefined)[0]?.friendlyName;

                          const full_key = parent + ":" + dimFriendlyName;
                          const color = colors[full_key];

                          return containsToolTipData(
                            full_key,
                            seriesFilter,
                            tooltipData
                          ) ? (
                            index < props.dimensions.length - 1 ? (
                              <Fragment key={full_key}>
                                <div
                                  className="standard-tooltip__item"
                                  style={{
                                    marginLeft: (index + 1) * 15 + "px",
                                  }}
                                >
                                  <span
                                    className={clsx(
                                      "standard-tooltip__key",
                                      "text-capitalize"
                                    )}
                                    // style={{ color: colors[metric] }}
                                  >
                                    {dim_key}
                                  </span>
                                  <br />
                                </div>
                                {renderItem(index + 1, full_key)}
                              </Fragment>
                            ) : (
                              <div
                                className="standard-tooltip__item"
                                key={full_key}
                                style={{ marginLeft: (index + 1) * 15 + "px" }}
                              >
                                <span
                                  className="standard-tooltip__icon"
                                  style={{ background: color }}
                                />
                                <span
                                  className={clsx(
                                    "standard-tooltip__key",
                                    "text-capitalize"
                                  )}
                                  style={{ color: color }}
                                >
                                  {dim_key}
                                </span>
                                <br />
                                <span className="standard-tooltip__value">
                                  {/* if it's a prediction.. doesn't show value (because there's nothing to show) */}
                                  {isPrediction
                                    ? ""
                                    : formatQueryMetric(
                                        parent,
                                        singleTooltipData.payload[full_key],
                                        globalState.queryMetrics
                                      )}
                                </span>
                              </div>
                            )
                          ) : null;
                        });
                      };

                      return (
                        <div className="standard-tooltip">
                          {tooltipData && (
                            <header className="standard-tooltip__header">
                              <span className="standard-tooltip__key-header">
                                Date:{" "}
                                <strong>
                                  {moment(new Date(currentDatePoint))
                                    .utc()
                                    .format(
                                      formatDate(props.granularity.value, true)
                                    )}
                                </strong>
                              </span>
                            </header>
                          )}
                          <main className="standard-tooltip__module">
                            {props.metrics
                              .filter(metricIsNotAPrediction)
                              .map((metric, j) => {
                                return containsToolTipData(
                                  metric,
                                  seriesFilter,
                                  tooltipData
                                ) ? (
                                  all_dim_keys.length === 0 ? (
                                    <QueryBuilderTooltipMetric
                                      key={j}
                                      metric={metric}
                                      currentTooltipData={
                                        singleTooltipData.payload
                                      }
                                      isPrediction={isPrediction}
                                      color={colors[metric]}
                                      metricFriendlyName={
                                        props.metricMap[metric].friendlyName
                                      }
                                      metrics={globalState.queryMetrics}
                                    />
                                  ) : (
                                    <Fragment key={metric}>
                                      <div className="standard-tooltip__item">
                                        <span
                                          className={clsx(
                                            "standard-tooltip__key",
                                            "text-capitalize"
                                          )}
                                        >
                                          {props.metricMap[metric].friendlyName}
                                        </span>
                                      </div>
                                      {renderItem(0, metric, isPrediction)}
                                    </Fragment>
                                  )
                                ) : null;
                              })}
                          </main>
                          {
                            // in the case that this is a prediction value for a dimension
                            isPrediction && (
                              <QueryBuilderTooltipPrediction
                                tooltipPayload={{
                                  ...extractPredictionData({
                                    ...singleTooltipData,
                                    dataKey: dataKey,
                                  }),
                                  color: colors[dataKey.split(".")[0]],
                                }}
                                metrics={globalState.queryMetrics}
                              />
                            )
                          }
                        </div>
                      );
                    }
                  }}
                />
              }
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="col-2">
        <Legend data={lData} onChangeSelection={onChangeSelection} />
      </div>
    </div>
  );
});

export default QueryBuilderBarChart;
